import React, { useEffect, useState, useRef } from 'react';
import { AppBar, AppBarSection, AppBarSpacer } from '@progress/kendo-react-layout';
import { Badge, BadgeContainer } from '@progress/kendo-react-indicators';
import { Button } from '@progress/kendo-react-buttons';
import { Popup } from "@progress/kendo-react-popup";

import UserMenu from 'views/main/UserMenu.jsx';
import InstancePicker from 'views/main/InstancePicker.jsx';
import RolePicker from 'views/main/RolePicker.jsx';
import CachedMessages from 'views/main/CachedMessages.jsx';

const FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])'
].join(',');

const getFocusableElements = (root, excludePopupElements = false) => {
	if (!root) return [];

	const elements = [];

	if (root.matches?.(FOCUSABLE_SELECTOR)) elements.push(root);
	elements.push(...root.querySelectorAll(FOCUSABLE_SELECTOR));
	const documentHasLayout = root.ownerDocument?.body?.getClientRects().length > 0;

	return elements.filter((element) =>
		element.tabIndex >= 0 &&
		(
			!documentHasLayout ||
			element.getClientRects().length > 0
		) &&
		(
			!excludePopupElements ||
			!element.closest('.k-animation-container, .k-popup')
		)
	);
};

const getToolbarTrigger = (container) => {
	if (!container) return null;

	return container.matches?.(FOCUSABLE_SELECTOR)
		? container
		: container.querySelector(FOCUSABLE_SELECTOR);
};

const focusNextApplicationElement = (triggerContainer) => {
	const trigger = getToolbarTrigger(triggerContainer);

	if (!trigger) return;

	const applicationElements = getFocusableElements(document.body, true);
	const triggerIndex = applicationElements.indexOf(trigger);

	if (triggerIndex === -1 || applicationElements.length < 2) return;

	const nextIndex = (triggerIndex + 1) % applicationElements.length;
	applicationElements[nextIndex].focus();
};

export default function MainAppBarView(props) {

	const { handleNavToggle, navigateTo, sessionChanging, sessionChanged, messageInfo={}, hasFilters, handleFilterClicked, handleFiltersCleared } = props;
	
	const notificationRef = useRef(null);
	const messagePopupRef = useRef(null);
	const instancePickerRef = useRef(null);
	const rolePickerRef = useRef(null);
	const userMenuRef = useRef(null);
	const [showNotifications, setShowNotifications] = useState(false);		
	const notificationsClicked = (e) => {
		e.stopPropagation();
		setShowNotifications((visible) => !visible);
	};

	const notificationsKeyDown = (e) => {
		if (e.key !== "Enter" && e.key !== " " && e.key !== "ArrowDown")
			return;

		e.preventDefault();
		e.stopPropagation();
		setShowNotifications(true);
	};

	const notificationsBlurred = () => {
		requestAnimationFrame(() => {
			const focusedElement = document.activeElement;
			const focusIsOnButton = notificationRef.current && notificationRef.current.contains(focusedElement);
			const focusIsInPopup = messagePopupRef.current && messagePopupRef.current.contains(focusedElement);

			if (!focusIsOnButton && !focusIsInPopup)
				setShowNotifications(false);
		});
	};

	const notificationsPopupKeyDown = (e) => {
		if (e.key !== 'Tab') return;

		const popupElements = getFocusableElements(messagePopupRef.current)
			.filter((element) => element !== messagePopupRef.current);
		const focusedElement = document.activeElement;
		const firstElement = popupElements[0];
		const lastElement = popupElements[popupElements.length - 1];
		const leavingBackwards = e.shiftKey && (
			focusedElement === messagePopupRef.current ||
			focusedElement === firstElement
		);
		const leavingForwards = !e.shiftKey && (
			popupElements.length === 0
				? focusedElement === messagePopupRef.current
				: focusedElement === lastElement
		);

		if (!leavingBackwards && !leavingForwards) return;

		e.preventDefault();
		e.stopPropagation();
		setShowNotifications(false);

		if (leavingBackwards) {
			notificationRef.current?.focus();
		} else {
			focusNextApplicationElement(notificationRef.current);
		}
	};

	const portaledMenuKeyDown = (e, triggerContainer) => {
		if (
			e.key !== 'Tab' ||
			triggerContainer?.contains(e.target)
		) {
			return;
		}

		// Kendo focuses the popup wrapper first. Allow the first forward Tab to
		// enter the menu; subsequent Tab presses from a menu control exit it.
		if (
			!e.shiftKey &&
			e.target.querySelector?.('[role="menuitem"]')
		) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();

		if (e.shiftKey) {
			getToolbarTrigger(triggerContainer)?.focus();
		} else {
			focusNextApplicationElement(triggerContainer);
		}
	};
	
	const {numberOfUnread, activeSystemMessage} = messageInfo;
	
	const documentClickListener = (e) => {
		showNotifications ? setShowNotifications(false) : null;
	};
	
	useEffect(() => {
    	document.addEventListener("click", documentClickListener);
    	if (showNotifications && messagePopupRef.current)
    		messagePopupRef.current.focus();
		
		return () => { document.removeEventListener("click", documentClickListener); }
  	}, [showNotifications, messagePopupRef]);
  		
   	return (
      		<header role="banner">
        		<AppBar className="constituo-app-bar">
          			
          			<AppBarSection className="title">
						<Button style={{
								'color': '#ffffff',
								'borderColor': '#2C343B',
								'backgroundColor': '#2C343B'
							}}
							tabIndex="0"
							aria-label="Expand Navigation"
							icon="menu" 
							look="flat" 
							onClick={handleNavToggle} 
						/>
						<div
							role="button"
						    tabIndex={0}
						    aria-label="Edit Filters"
						    title="Edit Filters"
						    onClick={handleFilterClicked}
						    onKeyDown={(e) => {
						      	if (e.key === "Enter" || e.key === " ") 
						        	handleFilterClicked(e);
						    }}
						    style={{
						      	display: "inline-flex",
						      	alignItems: "center",
						      	justifyContent: "center",
						      	width: "40px",
						      	cursor: "pointer",
						      	fontSize: "10pt",
								height: "28px"
						    }}
						    className="k-icon fa fa-small fa-filter"
						>
						</div>
						{hasFilters() && (
						<div
							role="button"
						   	tabIndex={0}
						    aria-label="Clear Filters"
						    title="Clear Filters"
						    onClick={handleFiltersCleared}
						    onKeyDown={(e) => {
						      	if (e.key === "Enter" || e.key === " ") 
						        	handleFiltersCleared(e);
						    }}
						    style={{
						      	display: "inline-flex",
						      	alignItems: "center",
						      	justifyContent: "center",
						      	width: "40px",
						      	cursor: "pointer",
						      	fontSize: "10pt",
								height: "28px"
						    }}
						    className="k-icon fa fa-small fa-cancel"
						 >
						 </div>							  
						 )}
          			</AppBarSection>
          			
          			<AppBarSpacer style={{ width: 4 }} />
          			<AppBarSection>
          				<h1 className="title ubuntu">
          					<span
          						className="fa fa-fw fa-lg fa-cs-logo"
          						style={{
									  'fontSize': '32px', 
									  'lineHeight': '32px', 
									  'verticalAlign': 'middle',
									  'paddingRight': '10px'
								}}
          					/>
          					{PRODUCT_NAME}
          				</h1>
        			</AppBarSection>

          			<AppBarSpacer
            			style={{ width: 4 }}
         			/>
					<span className="k-appbar-separator" />
					
         			<AppBarSpacer
            			style={{ width: 4 }}
         			/>
			<AppBarSection className="appbar-items">
				<span
					ref={instancePickerRef}
					style={{ display: 'inline-flex', alignItems: 'center' }}
					onKeyDownCapture={(e) => portaledMenuKeyDown(e, instancePickerRef.current)}
				>
					<InstancePicker tabIndex="0" sessionChanging={sessionChanging} sessionChanged={sessionChanged} />
				</span>
			</AppBarSection>
          			
          			
          			{(activeSystemMessage !=null && activeSystemMessage.message!=null) && (  
						<>
							<AppBarSpacer/>						       			
		      				<AppBarSection>
		      					<div
		      						style={{
											  'backgroundColor': 'rgb(228, 151, 60)',
											  'lineHeight': '32px', 
											  'verticalAlign': 'middle',
											  'borderRadius': '10px',
											  'paddingLeft': '10px',
											  'paddingRight': '15px'
										}}>
									{(activeSystemMessage.priority !=null && activeSystemMessage.priority>=200) &&
				      					<span
			          						className="fa fa-fw fa-lg fa-info-circled"
			          						style={{
												  'fontSize': '20px',
												  'marginRight': '5px'
											}}
			          					/>
		          					}
		          					{(activeSystemMessage.priority !=null && activeSystemMessage.priority<200) &&
				      					<span
			          						className="fa fa-fw fa-lg fa-attention-circled"
			          						style={{
												  'fontSize': '20px',
												  'marginRight': '5px'
											}}
			          					/>
		          					}
			      					<span
			      						style={{
											  'fontSize': '16px',
											  'fontWeight': 'bold'
										}}
			      					>
			      					{activeSystemMessage.message}
			      					</span>
		      					</div>
		        			</AppBarSection>
		        		</>
        			)}
          			
          			
         			<AppBarSpacer />
          			
          			<AppBarSection className="user-actions">
						<span className="k-appbar-separator" />
						
						<AppBarSpacer
            				style={{ width: 4 }}
         				/>
         				
            			<button 
            				ref={notificationRef}
            				tabIndex="0"
            				className="k-button k-button-clear"
            				onClick={notificationsClicked}
						onKeyDown={notificationsKeyDown}
						onBlur={notificationsBlurred}
						aria-haspopup="dialog"
						aria-expanded={showNotifications}
            				title="Recent Notifications"
            			>
              				<BadgeContainer>
                				<span className="k-icon k-i-bell" title="Recent Notifications"/>
              				</BadgeContainer>
							<Popup 
								anchor={notificationRef.current}
								show={showNotifications} 
								popupClass={"popup-content"}
								style={{ marginLeft: -10 }}
							>
								<div
									ref={messagePopupRef}
									tabIndex="0"
									onKeyDown={notificationsPopupKeyDown}
									onBlur={notificationsBlurred}
								>
								<CachedMessages
									closeNotifications={() => setShowNotifications(false)}
								/>
								</div>
							</Popup>
            			</button>
          			</AppBarSection>

					<AppBarSection>
						<span
							ref={rolePickerRef}
							style={{ display: 'inline-flex', alignItems: 'center' }}
							onKeyDownCapture={(e) => portaledMenuKeyDown(e, rolePickerRef.current)}
						>
							<RolePicker tabIndex="0" sessionChanging={sessionChanging} sessionChanged={sessionChanged} />
						</span>
					</AppBarSection>

          			<AppBarSpacer
            			style={{ width: 5 }}
         			/>
					<AppBarSection>
						<span
							ref={userMenuRef}
							style={{ display: 'inline-flex', alignItems: 'center' }}
							onKeyDownCapture={(e) => portaledMenuKeyDown(e, userMenuRef.current)}
						>
							<UserMenu 
								tabIndex="0"
								navigateTo={navigateTo} 
								unreadMessages={numberOfUnread}
								sessionChanging={sessionChanging}
								sessionChanged={sessionChanged} 
							/>
						</span>
					</AppBarSection>

        		</AppBar>
      		</header>
    	);
}
